import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Plus, Loader2, Info, Sparkles, Wand2 } from "lucide-react";
import { AiDraftDialog, AiImproveDialog } from "@/components/sop/AiDialogs";
import { ChangeSummaryDialog } from "@/components/sop/ChangeSummaryDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, ROLES } from "@/lib/constants";
import { AudienceField } from "@/components/AudienceField";
import { PasteHandbook } from "@/components/sop/PasteHandbook";
import { PageHeader } from "@/components/Badges";
import { LinkedFormsField } from "@/components/forms/RelatedForms";

const EMPTY = { title: "", category: "", summary: "", purpose: "", applicable_roles: [], audience_region_ids: [], steps: [{ title: "", detail: "" }] };

function StepEditor({ step, index, total, onChange, onMove, onRemove }) {
  return (
    <div className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={`sop-editor-step-${index + 1}`}>
      <div className="flex items-center justify-between">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-lasso-ink font-display text-xs font-bold text-white">{index + 1}</span>
        <div className="flex gap-1">
          <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" data-testid={`sop-editor-step-${index + 1}-up`}><ArrowUp className="h-4 w-4" /></button>
          <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} className="rounded-md p-2 text-zinc-500 hover:bg-lasso-tint disabled:opacity-30" data-testid={`sop-editor-step-${index + 1}-down`}><ArrowDown className="h-4 w-4" /></button>
          <button type="button" disabled={total === 1} onClick={onRemove} className="rounded-md p-2 text-zinc-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30" data-testid={`sop-editor-step-${index + 1}-remove`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <Input className="mt-4" placeholder="Step title (e.g. Shut off water at the closest valve)" value={step.title} onChange={(e) => onChange({ ...step, title: e.target.value })} data-testid={`sop-editor-step-${index + 1}-title`} />
      <Textarea className="mt-3" rows={3} placeholder="Detail — what to do, how, and what to watch out for" value={step.detail} onChange={(e) => onChange({ ...step, detail: e.target.value })} data-testid={`sop-editor-step-${index + 1}-detail`} />
    </div>
  );
}

export default function SopEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiDraft, setAiDraft] = useState(false);
  const [aiImprove, setAiImprove] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const isPublished = original?.status === "published";

  const applyDraft = (d) => {
    setForm((f) => ({ ...f, title: d.title, summary: d.summary, purpose: d.purpose, steps: d.steps.length ? d.steps : f.steps, category: d.category, applicable_roles: d.applicable_roles }));
    setAiDraft(false);
    setAiApplied(true);
    toast.success("AI draft loaded into the editor — review it, then save");
  };

  useEffect(() => {
    if (!id) return;
    api.get(`/sops/${id}`).then(({ data }) => {
      setOriginal(data);
      setForm({ title: data.title, category: data.category, summary: data.summary, purpose: data.purpose, applicable_roles: data.applicable_roles, audience_region_ids: data.audience_region_ids || [], steps: data.steps });
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStep = (i, s) => set("steps", form.steps.map((x, j) => (j === i ? s : x)));
  const moveStep = (i, d) => {
    const steps = [...form.steps];
    [steps[i], steps[i + d]] = [steps[i + d], steps[i]];
    set("steps", steps);
  };
  const toggleRole = (r) => set("applicable_roles", form.applicable_roles.includes(r) ? form.applicable_roles.filter((x) => x !== r) : [...form.applicable_roles, r]);

  const save = async (publish, changeSummary) => {
    if (!form.title.trim() || !form.category) return toast.error("Title and category are required");
    if (!form.applicable_roles.length) return toast.error("Choose at least one applicable role");
    if (form.steps.some((s) => !s.title.trim())) return toast.error("Every step needs a title");
    if (isPublished && changeSummary === undefined) return setSummaryOpen(true);
    setSaving(true);
    try {
      const payload = { ...form, change_note: changeNote || null, status: publish ? "published" : null, change_summary: changeSummary || null };
      const { data } = id ? await api.put(`/sops/${id}`, payload) : await api.post("/sops", { ...payload, status: publish ? "published" : "draft" });
      toast.success(id ? `Saved · now v${data.version}` : "SOP created");
      navigate(`/sops/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <Link to={id ? `/sops/${id}` : "/sops"} className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="sop-editor-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <div className="mt-6">
        <PageHeader
          eyebrow={id ? `Editing · v${original?.version ?? ""}` : "New SOP"}
          title={id ? form.title || "Edit SOP" : "Create an SOP"}
          actions={
            <>
              {!id && <PasteHandbook />}
              <button type="button" onClick={() => setAiDraft(true)} className="inline-flex items-center gap-2 rounded-full bg-lasso-sky px-5 py-2.5 text-sm font-semibold text-lasso-ink hover:bg-lasso-sky/80" data-testid="sop-editor-ai-draft-button">
                <Sparkles className="h-4 w-4" /> Draft with AI
              </button>
              {(id || form.steps.some((s) => s.title.trim())) && (
                <button type="button" onClick={() => form.title.trim() && form.category ? setAiImprove(true) : toast.error("Add a title and category first")} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-5 py-2.5 text-sm font-semibold hover:border-lasso-ink" data-testid="sop-editor-ai-improve-button">
                  <Wand2 className="h-4 w-4" /> Improve with AI
                </button>
              )}
            </>
          }
        />
      </div>
      {isPublished && (
        <ChangeSummaryDialog open={summaryOpen} sopId={id} form={{ ...form, change_note: changeNote || null }} nextVersion={original.version + 1} saving={saving}
          onCancel={() => setSummaryOpen(false)} onConfirm={(bullets) => save(false, bullets)} />
      )}
      <AiDraftDialog open={aiDraft} onClose={() => setAiDraft(false)} initial={form} onDraft={applyDraft} />
      <AiImproveDialog open={aiImprove} onClose={() => setAiImprove(false)} form={form} onAccept={(s) => { applyDraft({ ...s, category: form.category, applicable_roles: form.applicable_roles }); setAiImprove(false); }} />
      {aiApplied && (
        <div className="mb-8 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="sop-editor-ai-notice">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0" /> AI-generated content loaded. Review and edit every step — nothing is saved or published until you click a save button.
        </div>
      )}

      {isPublished && (
        <div className="mb-8 flex gap-3 rounded-xl border border-lasso-sky bg-lasso-tint p-4 text-sm text-lasso-navy" data-testid="sop-editor-version-notice">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          This SOP is published. Saving will create <strong className="mx-1">v{original.version + 1}</strong> and keep v{original.version} in version history.
        </div>
      )}

      <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input className="h-11" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="sop-editor-title-input" />
          </div>
          <div className="space-y-2">
            <Label>Summary</Label>
            <Textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="sop-editor-summary-input" />
          </div>
          <div className="space-y-2">
            <Label>Purpose</Label>
            <Textarea rows={2} value={form.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="sop-editor-purpose-input" />
          </div>
          <div>
            <Label>Steps</Label>
            <div className="mt-3 space-y-3">
              {form.steps.map((s, i) => (
                <StepEditor key={i} step={s} index={i} total={form.steps.length} onChange={(v) => setStep(i, v)} onMove={(d) => moveStep(i, d)} onRemove={() => set("steps", form.steps.filter((_, j) => j !== i))} />
              ))}
            </div>
            <button type="button" onClick={() => set("steps", [...form.steps, { title: "", detail: "" }])} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-lasso-line py-4 text-sm font-semibold text-zinc-500 transition-colors hover:border-lasso-ink hover:text-lasso-ink" data-testid="sop-editor-add-step">
              <Plus className="h-4 w-4" /> Add step
            </button>
          </div>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-10 lg:self-start">
          <LinkedFormsField kind="sops" id={id} />
          <div className="space-y-5 rounded-2xl border border-lasso-line bg-white p-5">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="sop-editor-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`sop-editor-category-${c.value.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c.value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <AudienceField value={form.audience_region_ids} onChange={(v) => setForm((f) => ({ ...f, audience_region_ids: v }))} testId="sop-editor-audience" />
            <div className="space-y-3">
              <Label>Applicable roles</Label>
              {ROLES.map((r) => (
                <label key={r.value} className="flex cursor-pointer items-center gap-3 text-sm">
                  <Checkbox checked={form.applicable_roles.includes(r.value)} onCheckedChange={() => toggleRole(r.value)} data-testid={`sop-editor-role-${r.value}`} />
                  {r.label}
                </label>
              ))}
            </div>
            {id && (
              <div className="space-y-2">
                <Label>Change note <span className="text-muted-foreground">(optional)</span></Label>
                <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="What changed?" data-testid="sop-editor-change-note" />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {isPublished ? (
              <button disabled={saving} onClick={() => save(false)} className="flex items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-3 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="sop-editor-save-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save as v{original.version + 1}
              </button>
            ) : (
              <>
                <button disabled={saving} onClick={() => save(true)} className="flex items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-3 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="sop-editor-publish-button">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save & publish
                </button>
                <button disabled={saving} onClick={() => save(false)} className="rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold hover:border-lasso-ink disabled:opacity-60" data-testid="sop-editor-save-draft-button">
                  Save as draft
                </button>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
